import { useSyncExternalStore } from 'react';
import { env } from '@/config/env';

/**
 * Nom de la société qui exploite l'instance.
 *
 * Les écrans de l'ADV désignaient la partie interne par la chaîne littérale
 * « ubeefy », dans trois fichiers. Une ESN qui installe la plateforme voyait
 * donc le nom de l'éditeur sur ses propres propositions et factures, face au
 * nom réel de son client.
 *
 * Le nom vient de l'identité d'entreprise (`/parametres/entreprise`), et suit
 * la même mécanique que `lib/currency` : un état de module lu par des fonctions
 * pures de mise en forme, et un abonnement pour les composants. Le faire
 * descendre en propriété aurait demandé de traverser les colonnes de tableau,
 * qui ne sont pas des composants.
 *
 * Tant que l'identité n'est pas renseignée, le nom de l'application tient lieu
 * de repli — c'est ce qui s'affichait avant, sans le figer dans le code.
 */
let currentName = env.VITE_APP_NAME;

const listeners = new Set<() => void>();
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getCompanyName = (): string => currentName;

/** Un nom vide ou absent laisse le repli en place plutôt que d'effacer l'affichage. */
export const setCompanyName = (name: string | null | undefined): void => {
  const next = name?.trim() ? name.trim() : env.VITE_APP_NAME;
  if (next === currentName) return;
  currentName = next;
  listeners.forEach((listener) => listener());
};

export const useCompanyName = (): string =>
  useSyncExternalStore(subscribe, getCompanyName, () => env.VITE_APP_NAME);
